import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Button,
  Breadcrumbs,
  Container,
  LinearProgress,
  IconButton,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  Box,
  CircularProgress,
  Fade,
  Grow,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SpeakerImage from '../../media/Lamp Poses-01-1.png';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../hooks/useApi';
import { AuthContext } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';

function Index() {
  const jApi = useApi();
  const navigate = useNavigate();
  const { getUser } = useContext(AuthContext);
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchParams] = useSearchParams();
  const [justConsented, setJustConsented] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [consentGranted, setConsentGranted] = useState(false);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  const fetchData = async () => {
    try {
      setErrorMessage(null);

      const adminConsent = searchParams.get('adminConsent');
      if (adminConsent === 'true') {
        setJustConsented(true);
      }
    } catch (error) {
      setErrorMessage(error.toString());
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

 useEffect(() => {
  let intervalId = null;
const checkConsent = async () => {
  let hadError = false;

  try {
    if (pollAttempt === 0) {
      setIsLoading(true);
    }
    setErrorMessage(null);

    const selected_account = sessionStorage.getItem('account');

    const response = await jApi.request(
      'GET',
      'auth/validate_appreg_assessment/account/' + selected_account
    );

    console.log('Poll response:', response?.data);

    if (response?.data?.consent_granted === true) {
      console.log('Consent granted – redirecting to /assessment');
      clearInterval(intervalId);
      setIsLoading(false);
      navigate('/assessment', { replace: true });
      return;
    }

    setPollAttempt((prev) => prev + 1);
  } catch (err) {
    console.error('Polling error:', err);
    setErrorMessage(err.message || 'Failed to check consent status');
    hadError = true;
  } finally {
    // Turn off loading after first poll or if there was an error
    if (pollAttempt === 0 || hadError) {
      setIsLoading(false);
    }
  }
};
  // Start polling
  checkConsent(); // immediate first check
  intervalId = setInterval(checkConsent, 15000);

  return () => {
    if (intervalId) clearInterval(intervalId);
  };
}, [navigate, jApi]);


  const handleGrantConsent = () => {
    setIsLoading(true);

    const return_url = 'http://localhost:3006/assessment/consent?adminConsent=true';
    const consentUrl = `https://login.microsoftonline.com/common/oauth2/authorize?client_id=62f4c2ac-68ec-4fd8-bfa4-fcda23edc81a&response_type=code&redirect_uri=${encodeURIComponent(
      return_url
    )}&resource=https://graph.microsoft.com&prompt=select_account`;

    window.location.href = consentUrl;

    // Note: In production, you should encodeURIComponent the full redirect_uri
  };

  const handleRefresh = () => {
    console.log('Checking consent status...');
    setIsLoading(true);
    // You could call checkConsent() here directly if you want manual refresh
    setTimeout(() => setIsLoading(false), 1200); // demo delay
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Assessment Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    p: 3,
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Assessment Center
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Microsoft Permissions Required
                    </Typography>
                  </Box>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          <Grid size={12}>
            <Grow in timeout={800}>
              <Paper
                sx={{
                  p: 4,
                  mt: 3,
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                }}
              >
                <Stack direction="column" spacing={3} sx={{ maxWidth: 800, mx: 'auto' }}>
                  {justConsented && (
                    <Alert
                      severity="success"
                      icon={isPolling ? <CircularProgress size={20} /> : undefined}
                    >
                      Permissions were granted — waiting for Microsoft to apply them{' '}
                      {pollAttempt > 0 ? `(attempt ${pollAttempt})` : '(checking)'}.
                      <br />
                      This can take several minutes. Please keep this page open.
                    </Alert>
                  )}

                  <Typography variant="body1" paragraph>
                    Specific Microsoft permissions are required for CMA Technology to run your Microsoft 365 security assessment.
                  </Typography>

                  <Paper
                    sx={{
                      p: 4,
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.4)
                        : alpha(theme.palette.background.default, 0.6),
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Clicking below will redirect to the Microsoft "Permission request" page. Here you may grant the permissions necessary for the CMA Technology 365 Assessment.
                    </Typography>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      The assessment may begin once permissions are granted. It may take several minutes for the permissions to be granted.
                    </Typography>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleGrantConsent}
                        disabled={isLoading || justConsented}
                        sx={{ minWidth: 220, py: 1.5 }}
                      >
                        {isLoading ? (
                          <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1.5 }} />
                            Processing...
                          </>
                        ) : (
                          'Grant Permissions'
                        )}
                      </Button>

                      <Alert severity="info" sx={{ mt: 4 }}>
                        <strong>Note:</strong> A user with the <strong>Global Admin</strong> role is necessary to grant permissions.
                      </Alert>
                    </Box>
                  </Paper>

                  {consentGranted && (
                    <Alert severity="success">
                      Consent successfully granted! Redirecting or refreshing the application...
                    </Alert>
                  )}
                </Stack>
              </Paper>
            </Grow>
          </Grid>
          {errorMessage && (
            <Grid size={12}>
              <Fade in>
                <Paper
                  sx={{
                    p: 2,
                    background: alpha(theme.palette.error.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" color="error.main">
                    {errorMessage}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default Index;